import React from 'react';
import FeaturePageTemplate from '../components/FeaturePageTemplate';

const AutoDiscoveryPage = () => {
  const icon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const benefits = [
    "Automatic discovery of data assets eliminates manual catalog maintenance",
    "Complete visibility of your data landscape without additional configuration",
    "Time-saving automated schema and metadata detection",
    "Simplified data governance with automatic asset classification",
    "Comprehensive lineage mapping through automated discovery"
  ];

  const details = [
    {
      title: "Automated Schema Detection",
      description: "Qupid automatically scans and discovers your data landscape, detecting schemas, databases, tables, and columns across all connected sources.",
      items: [
        "Automatic detection of database schemas and table structures",
        "Column-level metadata discovery including data types and constraints",
        "Schema change detection and versioning",
        "Automated data dictionary generation"
      ]
    },
    {
      title: "ETL and Data Pipeline Discovery",
      description: "Identify and map data transformation and movement processes across your organization.",
      items: [
        "Automatic discovery of ETL jobs and dependencies",
        "Data pipeline mapping and visualization",
        "Change tracking for job definitions and schedules",
        "Performance monitoring of discovered jobs"
      ]
    },
    {
      title: "BI Dashboard Integration",
      description: "Automatically connect and discover analytics assets across your organization's BI tools.",
      items: [
        "Auto-discovery of dashboards, reports and visualizations",
        "Metadata extraction from BI objects",
        "Mapping of dashboard data dependencies",
        "Quality impact assessment on discovered dashboards"
      ]
    }
  ];

  const useCases = [
    {
      title: "Enterprise Data Catalog Creation",
      description: "Automatically build and maintain a comprehensive data catalog across your organization without manual effort, ensuring your data governance tools always reflect current reality."
    },
    {
      title: "Data Migration Planning",
      description: "Use auto-discovery to map your entire data landscape before migrations, ensuring no assets are overlooked and all dependencies are understood."
    },
    {
      title: "Governance and Compliance Automation",
      description: "Automatically identify and classify sensitive data across your organization to streamline compliance with regulations like GDPR, CCPA, and HIPAA."
    },
    {
      title: "BI Modernization",
      description: "Discover and map all existing reports and dashboards before modernizing your BI infrastructure, ensuring complete coverage in the transition."
    }
  ];

  return (
    <FeaturePageTemplate
      title="Auto Discovery"
      description="Automatically detect metadata across your entire data ecosystem, including schemas, databases, tables, columns, jobs, and BI dashboards, eliminating manual cataloging work."
      icon={icon}
      benefits={benefits}
      details={details}
      useCases={useCases}
      additionalContent={
        <div className="bg-gray-50 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4">Discovery Engine</h3>
          <p className="mb-4">
            Qupid's discovery engine operates with minimal performance impact on your systems. Our intelligent crawlers respect database load patterns and include throttling controls to ensure production systems are never impacted.
          </p>
          <p>
            The platform maintains a complete history of discovered assets, tracking changes over time to provide a full audit trail of your data evolution. Discovery schedules can be customized to match your organization's needs, from real-time monitoring of critical systems to weekly scans of stable data warehouses.
          </p>
        </div>
      }
    />
  );
};

export default AutoDiscoveryPage; 